// Single source of truth for "where does clicking this notification go".
// Used by the bell dropdown, the notifications page, and the web-push
// service worker click handoff (/notificaciones?open=<id>).
export interface RoutableNotification {
  type: string;
  target_type: string | null;
  target_id: string | null;
}

export function getNotificationRoute(notif: RoutableNotification): string | null {
  // Omnichannel inbox (WhatsApp) — checked BEFORE the intercambio branch,
  // which also uses target_type 'conversation' but means a sender profile id.
  // The support bot's unanswered question is answered on the Tickets board.
  if (notif.type === 'concierge_kb_gap') return `/administrador/tickets`;
  // Portal financing application → the super-admin list (no target row type).
  if (notif.type === 'financing_application') return `/administrador/ventas?tab=financiamiento`;
  // The bank moved the status of an application the agent filed → their list.
  if (notif.type === 'financing_status') return `/financiamiento`;
  // Agency blog post submitted for review → the approval queue (Blog tab).
  if (notif.type === 'blog_post_pending') return `/administrador/contenido`;
  // New Novedad (CRM news post) → the post; target_id is the blog_posts id,
  // which NovedadesPostPage accepts in place of the slug.
  if (notif.type === 'novedad') return notif.target_id ? `/novedades/${notif.target_id}` : `/novedades`;
  // The WhatsApp bot's alerts (concierge_handoff/escalation/visit/requirement)
  // carry a WhatsApp conversation id too — they used to fall through to the
  // intercambio inbox below.
  if (notif.type === 'wa_message' || notif.type === 'wa_health' || notif.type.startsWith('concierge_')) return `/mensajes`;
  // Intercambio message → its inbox (target_id holds the sender).
  if (notif.target_type === 'conversation') return `/intercambio/mensajes`;
  // Billing/account notifications (payment reminder, overdue, publish caps,
  // payment confirmation) all belong on the membership screen.
  if (notif.target_type === 'agency' || notif.target_type === 'payment') return `/membresia`;
  if (!notif.target_type || !notif.target_id) return null;
  switch (notif.target_type) {
    case 'property':
      return notif.type === 'new_match'
        ? `/propiedades/${notif.target_id}/coincidencias`
        : `/propiedades/${notif.target_id}`;
    case 'requirement':
      return notif.type === 'new_match'
        ? `/requerimientos/${notif.target_id}/coincidencias`
        : `/requerimientos/${notif.target_id}/editar`;
    case 'contact':
      // owner_authorization targets a property owner, who lives on the
      // /propietarios board — not /prospectos (leads).
      return notif.type === 'owner_authorization'
        ? `/propietarios?contactId=${notif.target_id}`
        : `/prospectos?contactId=${notif.target_id}`;
    case 'reminder':
      return `/calendario`;
    case 'appointment':
      return `/calendario`;
    case 'ticket':
      // See NotificationDropdown: status/reply notifications are the filer's,
      // and they have no admin panel.
      return notif.type === 'support_ticket_status' || notif.type === 'support_ticket_reply'
        ? `/soporte` : `/administrador/tickets`;
    default:
      return null;
  }
}
